import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { AuthImg } from "../components/AuthImg";
import { History, Loader2 } from "lucide-react";
import { fmtRoDate } from "../lib/dateFmt";
import VehicleTabsNav from "../components/VehicleTabsNav";

const TYPE_LABELS = {
  liquids: "🛢️ Control lichide",
  photos_actual: "📷 Fotografii actuale",
  tires: "🛞 Profil cauciucuri",
  cleaning: "✨ Curățenie",
  km_friday: "📊 Poza km",
};

export default function IstoricIntretinere() {
  const [entries, setEntries] = useState(null);
  const [carFilter, setCarFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    api.get("/me/car-maintenance", { params: { limit: 500 } })
      .then((r) => setEntries(r.data || []))
      .catch(() => setEntries([]));
  }, []);

  const cars = useMemo(() => {
    const map = new Map();
    (entries || []).forEach((e) => {
      if (e.car_id && !map.has(e.car_id)) {
        map.set(e.car_id, [e.car_plate, e.car_label].filter(Boolean).join(" · ") || e.car_id);
      }
    });
    return [...map.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label, "ro"));
  }, [entries]);

  const filtered = useMemo(() => (entries || []).filter((e) => {
    if (carFilter !== "all" && e.car_id !== carFilter) return false;
    if (typeFilter !== "all" && e.type !== typeFilter) return false;
    return true;
  }), [entries, carFilter, typeFilter]);

  return (
    <div data-testid="page-istoric-intretinere">
      <VehicleTabsNav />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><History size={28} /> Istoric întreținere mașini</h1>
          <p className="text-sm text-zinc-500 mt-1">Registrul controalelor săptămânale: lichide, poze km, cauciucuri, curățenie</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={carFilter}
            onChange={(e) => setCarFilter(e.target.value)}
            className="h-10 px-3 rounded-md border border-zinc-300 bg-white text-sm font-semibold"
            data-testid="istoric-filter-car"
          >
            <option value="all">Toate mașinile ({cars.length})</option>
            {cars.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-10 px-3 rounded-md border border-zinc-300 bg-white text-sm font-semibold"
            data-testid="istoric-filter-type"
          >
            <option value="all">Toate tipurile</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {entries === null && (
        <div className="text-center py-16 text-zinc-400 flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={18} /> Se încarcă registrul...</div>
      )}
      {entries !== null && filtered.length === 0 && (
        <div className="text-center py-16 text-zinc-400 italic">Nicio înregistrare de întreținere{carFilter !== "all" || typeFilter !== "all" ? " pentru filtrele alese" : ""}.</div>
      )}
      {entries !== null && filtered.length > 0 && (
        <>
          <div className="text-xs text-zinc-500 mb-2">{filtered.length} înregistrări</div>
          <ul className="space-y-3">
            {filtered.map((e) => (
              <li key={e.id} className="card-flat p-4" data-testid={`istoric-entry-${e.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap text-sm">
                  <div>
                    <strong>{TYPE_LABELS[e.type] || e.type}</strong>
                    <span className="text-zinc-500"> · 📅 {fmtRoDate(e.ts)}</span>
                  </div>
                  <div className="text-xs text-zinc-500">
                    🚙 {[e.car_plate, e.car_label].filter(Boolean).join(" · ") || "—"} · 👤 {e.user_label || "—"}{e.driver_label ? ` · 🧑‍✈️ Șofer: ${e.driver_label}` : ""}
                  </div>
                </div>
                {e.type === "km_friday" && e.data?.km && (
                  <div className="text-sm font-bold mt-1 text-orange-700">📊 {Number(e.data.km).toLocaleString("ro-RO")} km</div>
                )}
                {e.type === "tires" && (e.data?.fl_mm || e.data?.fr_mm) && (
                  <div className="text-xs mt-1">🛞 FS:{e.data?.fl_mm ?? "—"} FD:{e.data?.fr_mm ?? "—"} SS:{e.data?.rl_mm ?? "—"} SD:{e.data?.rr_mm ?? "—"} mm</div>
                )}
                {e.notes && <div className="text-xs italic text-zinc-600 mt-1">&bdquo;{e.notes}&rdquo;</div>}
                {(e.image_ids || []).length > 0 && (
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5 mt-2">
                    {e.image_ids.slice(0, 16).map((fid, idx) => (
                      <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="aspect-square block rounded overflow-hidden border border-zinc-200 hover:border-zinc-500">
                        <AuthImg fileId={fid} className="w-full h-full object-cover" alt={`Foto ${idx + 1}`} />
                      </a>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
